import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import Currency from "@mapmycustomers/shared/types/Currency";

import DemographicData from "@app/scene/map/types/DemographicData";
import { getCurrencies } from "@app/store/referenceData";
import { RootState } from "@app/store/rootReducer";

import styles from "./DemographyData.module.scss";

const messages = defineMessages({
  medianIncome: {
    id: "demographyData.medianIncome",
    defaultMessage: "Avg Median Income",
    description: "Data View - Avg Median Income",
  },
  source: {
    id: "demographyData.source",
    defaultMessage: "From US Census Bureau",
    description: "Data View - From US Census Bureau",
  },
  totalPopulation: {
    id: "demographyData.totalPopulation",
    defaultMessage: "Total Population",
    description: "Data View - Total Population",
  },
});

interface Props {
  className?: string;
  currencies: Currency[];
  data: Omit<DemographicData, "code" | "type">;
  theme?: "dark" | "light";
}

const DemographyData: React.FC<Props> = ({ className, currencies, data, theme = "dark" }) => {
  const intl = useIntl();
  const { income, population } = data;

  const isVisible = income !== undefined && population !== undefined;
  const currency = currencies?.find((currency) => currency.id === income?.currencyId);
  const getParentElement = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);

  return isVisible ? (
    <div className={cn(styles.container, className)}>
      <div className={styles.infoArea}>
        <div className={styles.dataRow}>
          <div className={styles.row}>
            <span className={cn(styles.title, { [styles.lightTitle]: theme === "light" })}>
              {intl.formatMessage(messages.medianIncome)}
            </span>
            <Tooltip
              getPopupContainer={getParentElement}
              title={intl.formatMessage(messages.source)}
            >
              <FontAwesomeIcon className={styles.info} icon={faInfoCircle} />
            </Tooltip>
          </div>
          <span className={cn(styles.data, { [styles.lightData]: theme === "light" })}>
            {income?.value != null
              ? intl.formatNumber(income?.value, {
                  currency: currency?.code,
                  minimumFractionDigits: 0,
                  style: "currency",
                })
              : ""}
          </span>
        </div>
        <div className={styles.dataRow}>
          <div className={styles.row}>
            <span className={cn(styles.title, { [styles.lightTitle]: theme === "light" })}>
              {intl.formatMessage(messages.totalPopulation)}
            </span>
            <Tooltip
              getPopupContainer={getParentElement}
              title={intl.formatMessage(messages.source)}
            >
              <FontAwesomeIcon className={styles.info} icon={faInfoCircle} />
            </Tooltip>
          </div>
          <span className={cn(styles.data, { [styles.lightData]: theme === "light" })}>
            {intl.formatNumber(population)}
          </span>
        </div>
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  currencies: getCurrencies(state),
});

export default connect(mapStateToProps)(DemographyData);
